import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
  CenteredSpinner,
} from '@wyz/ui';
import { BlobHelpers, invariant, SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { Resolver, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import {
  CriteriaBuilder,
  DropzoneAreaField,
  MatchedCustomersTable,
  SelectField,
  TextField,
} from '~/common/components';
import {
  Trans,
  useApplicationContext,
  useConfig,
  useI18n,
} from '~/common/context';
import { useBackTo } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { usePurchaseContext } from '~/purchase/context';
import {
  createDestockingPolicyForm,
  destockingPolicyForm,
} from '~/purchase/schemas';
import {
  ICreateDestockingPolicyForm,
  IDestockingPolicyImportError,
} from '~/purchase/types';
import { useManageSupplierPlatforms } from '~/common/api';
// eslint-disable-next-line @wyz/module/bad-module-import
import { ISupplierPlatform } from '~/connect/types';
import { PurchaseErrorCodesLookup } from '~/purchase/helpers';

type ContainerProps = {
  mode: 'CREATE' | 'UPDATE' | 'READ_ONLY';
  onSubmit: (formData: ICreateDestockingPolicyForm) => void;
  isSubmitting: boolean;
  errors: Array<IDestockingPolicyImportError>;
  defaultValues?: Partial<ICreateDestockingPolicyForm>;
};

function DestockingFormContainer(props: ContainerProps) {
  const { state } = usePurchaseContext();
  invariant(state.targetCustomer);
  const targetCustomer = state.targetCustomer;

  const manageSupplierPlatforms = useManageSupplierPlatforms({
    list: {
      // Never query with an empty list: `qs.stringify` drops empty arrays, the filter vanishes from
      // the query string and the endpoint answers with *every* platform distributor — which the
      // form below would then pre-select as the customer's own stocks.
      enabled:
        targetCustomer.internalStockIds.length > 0 ||
        props.mode === 'READ_ONLY',
      args: {
        supplierTypeIds: [SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR],
        supplierWyzApiIds: targetCustomer.internalStockIds,
      },
    },
  });

  // A disabled query never fetches, so this stays false and the "no stock" verdict renders at once.
  if (manageSupplierPlatforms.list.isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <DestockingPolicyForm
      {...props}
      supplierPlatforms={manageSupplierPlatforms.list.data?.data ?? []}
    />
  );
}

type Props = ContainerProps & {
  supplierPlatforms: ISupplierPlatform[];
};

function DestockingPolicyForm({
  mode,
  supplierPlatforms,
  onSubmit,
  isSubmitting,
  errors = [],
  defaultValues,
}: Props) {
  const backTo = useBackTo('..');
  const { apiUrl, tenantCode } = useConfig();
  const { auth } = useApplicationContext();
  const { t } = useI18n();
  const { state } = usePurchaseContext();
  invariant(state.targetCustomer);
  const targetCustomer = state.targetCustomer;
  const { id } = useParams<{ id: string }>();

  const stockOptions = supplierPlatforms.map(({ supplier }) => ({
    value: supplier.id,
    label: supplier.name,
  }));

  // Nothing to destock from: the customer declared no stock in « Mes stocks », or none of them is a
  // platform distributor. Only CREATE is blocked — an existing operation must stay editable and
  // closable whatever the customer's stocks look like today.
  const hasNoStock = mode === 'CREATE' && supplierPlatforms.length === 0;

  const methods = useForm<ICreateDestockingPolicyForm>({
    defaultValues: {
      name: '',
      supplierIds: supplierPlatforms.map(({ supplier }) => supplier.id),
      criteria: [],
      ...defaultValues,
    },
    // UPDATE and READ_ONLY validate against `destockingPolicyForm` (no `file` field);
    // the cast is safe because that schema only checks the fields it declares and
    // ignores the rest, so it is compatible with the wider CREATE form shape.
    resolver: zodResolver(
      mode === 'CREATE' ? createDestockingPolicyForm : destockingPolicyForm,
    ) as unknown as Resolver<ICreateDestockingPolicyForm>,
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData) => onSubmit(formData)}
      // Drives every shared field's read-only rendering (TextField, SelectField) and
      // hides CriteriaBuilder's add/remove controls — no per-field disabling needed.
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
      className='space-y-4'
    >
      {mode === 'UPDATE' && (
        <Alert variant='info' className='d-flex align-items-center mb-section'>
          <Icon
            icon='information-line'
            size='md'
            className='text-info-text me-3'
          />
          <Text heading='sm' color='info'>
            <Trans i18nKey='app.pricing_policy.destocking.edit.warning' />
          </Text>
        </Alert>
      )}
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='article-line' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.pricing_policy.destocking.form.general_information.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <TextField
                name='name'
                label={t(
                  'app.pricing_policy.destocking.form.general_information.name',
                )}
                type='text'
                size='sm'
                required
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='warehouse' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.pricing_policy.destocking.form.stocks.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <SelectField<true>
                name='supplierIds'
                label={t('app.pricing_policy.destocking.form.stocks.title')}
                options={stockOptions}
                isMulti
                maxShown={Infinity}
                wrapItems
                required
                isDisabled={mode !== 'CREATE'}
              />
            </Col>
          </Row>
          {hasNoStock && (
            <Alert
              variant='danger'
              className='d-flex align-items-center mb-section'
              id='destocking-no-stock-alert'
            >
              <Icon icon='warning-triangle' size='md' className='me-3' />
              <Text heading='sm' color='danger'>
                <Trans i18nKey='app.pricing_policy.destocking.create.no_internal_stock' />
              </Text>
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.pricing_policy.destocking.form.customers.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder
            name='criteria'
            visibilityFilter={{ customerIds: [targetCustomer.id] }}
          >
            <Row className='mt-4'>
              <Col md={12}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.DESTOCKING_PRICING_POLICY}
                  name='criteria'
                  queryOverrideParams={{ parentIds: [targetCustomer.id] }}
                />
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='article-line' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.pricing_policy.destocking.form.file.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          {mode !== 'CREATE' ? (
            <Row>
              <Col>
                <Text heading='sm' weight='bold'>
                  <Trans i18nKey='app.pricing_policy.destocking.form.file.imported_file' />
                </Text>
                <Button
                  id='download-destocking-file'
                  variant='link'
                  type='button'
                  onClick={() => {
                    invariant(id);
                    BlobHelpers.download({
                      url: `${apiUrl}/pricing-policies/api/tenants/${tenantCode}/destocking-pricing-policies/${id}/file`,
                      token: auth.user?.access_token ?? '',
                    }).catch(() => {});
                  }}
                >
                  {defaultValues?.uploadedFileName}
                  <Icon icon='download' className='ms-2' />
                </Button>
              </Col>
            </Row>
          ) : (
            <>
              <Row>
                <Col>
                  <DropzoneAreaField
                    name='file'
                    label=''
                    filesLimit={1}
                    acceptedFiles='XLSX'
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Alert
                    variant='warning'
                    className='d-flex align-items-center'
                  >
                    <Icon
                      icon='alert'
                      size='md'
                      className='text-warning-text me-3'
                    />
                    <Text heading='sm' color='warning'>
                      <Trans i18nKey='app.pricing_policy.destocking.form.file.price_warning' />
                    </Text>
                  </Alert>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    id='download-destocking-template'
                    variant='link'
                    type='button'
                    onClick={() => {
                      BlobHelpers.download({
                        url: `${apiUrl}/pricing-policies/api/tenants/${tenantCode}/destocking-pricing-policies/export-model`,
                        token: auth.user?.access_token ?? '',
                      }).catch(() => {});
                    }}
                  >
                    <Trans i18nKey='app.common.buttons.download_template' />
                    <Icon icon='download' className='ms-2' />
                  </Button>
                </Col>
              </Row>
              {errors.length > 0 && (
                <>
                  {errors.map((error, index) => (
                    <Alert variant='danger' key={index}>
                      <Icon icon='warning-triangle' />
                      <Trans
                        i18nKey={PurchaseErrorCodesLookup[error.code]}
                        values={error.context}
                      />
                    </Alert>
                  ))}
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* No footer in READ_ONLY: there is nothing to submit, and the page's own BackLink
          already provides the way out — same treatment as redistribution/$id/detail.tsx.
          Omitting the submit control also means `submitHandler` can never fire. */}
      {mode !== 'READ_ONLY' && (
        <Card className='mb-section'>
          <Card.Body>
            <Flex justifyContent='between'>
              <Button
                id='cancel'
                as={Link}
                variant='secondary'
                type='button'
                to={backTo}
              >
                <Trans i18nKey='app.common.button.cancel' />
              </Button>
              <Button
                id='submit'
                variant='primary'
                type='submit'
                isLoading={isSubmitting}
                disabled={hasNoStock}
              >
                <Trans
                  i18nKey={
                    mode === 'CREATE'
                      ? 'app.common.button.create'
                      : 'app.common.button.save'
                  }
                />
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
}

export default DestockingFormContainer;
